import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { ContactDto } from '@/services/contact/interfaces/contact.interface'

export const useContactStore = defineStore('contact', () => {
  const contactDataCache = ref<Map<string, ContactDto>>(new Map())
  const loadingContactData = ref<Set<string>>(new Set())

  const getContactData = (phone: string): ContactDto | null => {
    return contactDataCache.value.get(phone) || null
  }

  const isContactLoading = (phone: string): boolean => {
    return loadingContactData.value.has(phone)
  }

  const hasContactInCache = (phone: string): boolean => {
    return contactDataCache.value.has(phone)
  }

  const getContactFromCache = (phone: string): ContactDto | null => {
    return contactDataCache.value.get(phone) || null
  }

  const updateContactInCache = (phone: string, contactData: ContactDto) => {
    contactDataCache.value.set(phone, contactData)
  }

  const removeContactFromCache = (phone: string) => {
    contactDataCache.value.delete(phone)
    loadingContactData.value.delete(phone)
  }

  const clearContactCache = () => {
    contactDataCache.value.clear()
    loadingContactData.value.clear()
  }

  const preloadContacts = async (phones: string[]) => {

    const promises = phones
      .filter((phone) => !contactDataCache.value.has(phone) && !loadingContactData.value.has(phone))
      .map((phone) => getContactData(phone))

    try {
      await Promise.all(promises)
    } catch {}
  }

  const getCacheStats = () => {
    return {
      cached: contactDataCache.value.size,
      loading: loadingContactData.value.size,
      phones: Array.from(contactDataCache.value.keys()),
    }
  }

  const getAllCachedContacts = (): ContactDto[] => {
    return Array.from(contactDataCache.value.values())
  }

  const filterContacts = (query: string): ContactDto[] => {
    const lowerQuery = query.toLowerCase()
    return getAllCachedContacts().filter(
      (contact) =>
        contact.name?.toLowerCase().includes(lowerQuery) ||
        contact.phone?.includes(query) ||
        contact.email?.toLowerCase().includes(lowerQuery),
    )
  }

  return {
    contactDataCache,
    loadingContactData,
    getContactData,
    isContactLoading,
    hasContactInCache,
    getContactFromCache,
    updateContactInCache,
    removeContactFromCache,
    clearContactCache,
    preloadContacts,
    getCacheStats,
    getAllCachedContacts,
    filterContacts,
  }
})

